/**
 * Highlighting the learner's own vocabulary inside an example sentence.
 *
 * A saved word's example sentence is where the connections between words
 * live: "She took a **sagacious** decision" tells you nothing about the
 * other words in the deck, but "She showed **sagacity** when she was
 * **reluctant** to compromise" shows that two saved words share a sentence.
 * Highlighting the deck words that appear inside the example turns those
 * sentences into a map of the deck instead of isolated lines of text.
 *
 * The matching is deliberately forgiving (case-insensitive, plural/verb
 * inflections, multi-word phrases) but it is *only* applied to words the
 * learner actually saved, so a miss costs nothing and a false positive is
 * visible: it would highlight a word that isn't in the deck.
 */

/** A run of example text, either plain or a highlighted deck word. */
export interface ExampleSegment {
    text: string;
    /** The deck word this run matched (undefined for plain text). */
    word?: string;
    /** True when the run is the subject word of the entry itself. */
    isSubject?: boolean;
}

/**
 * Words that are too common to be worth highlighting. A learner rarely saves
 * "the" or "and", but a topic's vocabulary list can still contain a short
 * function word ("use", "get", "own"), and highlighting every occurrence of
 * those in a sentence turns the line into noise.
 */
const STOP_WORDS = new Set([
    "a", "an", "and", "are", "as", "at", "be", "been", "but", "by", "can",
    "did", "do", "does", "for", "from", "had", "has", "have", "he", "her",
    "his", "how", "i", "if", "in", "is", "it", "its", "me", "my", "no", "not",
    "of", "on", "or", "our", "out", "own", "she", "should", "so", "some",
    "such", "than", "that", "the", "their", "them", "then", "there", "these",
    "they", "this", "to", "too", "up", "us", "was", "we", "were", "what",
    "when", "which", "who", "will", "with", "would", "you", "your",
]);

/** Minimum length of a single word we are willing to highlight. */
const MIN_WORD_LENGTH = 3;

const escapeRegex = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const VOWELS = new Set(["a", "e", "i", "o", "u"]);

/**
 * Whether English doubles the final consonant before `-ing`/`-ed`/`-er`/
 * `-est`: commit → committing, plan → planning, stop → stopping.
 *
 * The rule is approximate (English doubles after a short vowel in a stressed
 * syllable), but the CVC shape it looks for — consonant, vowel, single
 * consonant — covers the words that matter and leaves "visit → visiting"
 * and "open → opening" alone.
 */
const doublesFinalConsonant = (word: string): boolean => {
    if (word.length < 3) return false;
    const last = word[word.length - 1];
    const middle = word[word.length - 2];
    const first = word[word.length - 3];
    if (VOWELS.has(last) || "wxy".includes(last)) return false;
    if (!VOWELS.has(middle)) return false;
    return !VOWELS.has(first) && !/[aeiou]/.test(first);
};

/**
 * Regex for a word plus the inflections that still mean the same word:
 * plural `-s`/`-es`, past `-ed`, gerund `-ing`, comparative `-er`, adverb
 * `-ly`, superlative `-est`. Words ending in a silent `e` also get the
 * stem-without-`e` forms (commit → committing), which is why the pattern is
 * built per word instead of being one global suffix rule.
 */
const wordPattern = (word: string): string => {
    const escaped = escapeRegex(word);
    const parts = [`\\b${escaped}(?:s|es|ed|ing|er|est|ly|d)?\\b`];
    if (escaped.endsWith("e")) {
        parts.push(`\\b${escaped.slice(0, -1)}(?:ing|ed|er|est)\\b`);
    }
    if (doublesFinalConsonant(word)) {
        const last = escaped[escaped.length - 1];
        parts.push(`\\b${escaped}${last}(?:ing|ed|er|est)\\b`);
    }
    return parts.join("|");
};

/**
 * Regex for a multi-word phrase: the words themselves are matched literally,
 * but the whitespace between them is flexible so "sagacious decision" still
 * matches "sagacious  decision" or a line break in the middle.
 */
const phrasePattern = (phrase: string): string =>
    `\\b${phrase.split(/\\s+/).map(escapeRegex).join("\\s+")}\\b`;

/**
 * Compiled matchers are cached per word. A deck page renders dozens of
 * examples against the same word list, and recompiling the same regex for
 * every card is the difference between a snappy deck and a janky one.
 */
const MATCHER_CACHE = new Map<string, RegExp>();
const MATCHER_CACHE_LIMIT = 800;

const matcherFor = (word: string): RegExp => {
    const cached = MATCHER_CACHE.get(word);
    if (cached) return cached;

    const regex = word.includes(" ")
        ? new RegExp(phrasePattern(word), "gi")
        : new RegExp(wordPattern(word), "gi");

    if (MATCHER_CACHE.size >= MATCHER_CACHE_LIMIT) {
        // Cheap eviction: drop the oldest entry. The cache is a performance
        // aid, never a correctness dependency.
        const oldest = MATCHER_CACHE.keys().next();
        if (!oldest.done) MATCHER_CACHE.delete(oldest.value);
    }
    MATCHER_CACHE.set(word, regex);
    return regex;
};

/**
 * Whether a word is worth highlighting at all.
 *
 * The subject word of the entry is always worth it — that is the whole point
 * of the example — so it skips the stop-word and length filters.
 */
const isHighlightable = (word: string, isSubject: boolean): boolean => {
    if (!word) return false;
    if (isSubject) return true;
    if (word.includes(" ")) return true;
    if (word.length < MIN_WORD_LENGTH) return false;
    return !STOP_WORDS.has(word);
};

interface Match {
    start: number;
    end: number;
    word: string;
    isSubject: boolean;
}

/**
 * Find every occurrence of `words` inside `text`.
 *
 * Overlapping matches are resolved in favour of the longest one (a phrase
 * beats the single word it contains), and ties are resolved in favour of the
 * earlier match, so the result is a stable, non-overlapping list.
 */
const findMatches = (text: string, lower: string, candidates: { word: string; isSubject: boolean }[]): Match[] => {
    const matches: Match[] = [];
    const seen = new Set<string>();

    for (const candidate of candidates) {
        const { word, isSubject } = candidate;
        if (!word || seen.has(word)) continue;
        seen.add(word);

        if (!isHighlightable(word, isSubject)) continue;

        // Cheap pre-filter: a word can only appear if its first three
        // characters do. This skips almost every word in a large deck
        // without touching a regex.
        const prefix = word.slice(0, 3);
        if (prefix && !lower.includes(prefix)) continue;

        const regex = new RegExp(matcherFor(word).source, "gi");
        let m: RegExpExecArray | null;
        while ((m = regex.exec(lower)) !== null) {
            if (m[0].length === 0) {
                regex.lastIndex++;
                continue;
            }
            matches.push({ start: m.index, end: m.index + m[0].length, word, isSubject });
        }
    }

    // Longest match wins; earlier match breaks the tie.
    matches.sort((a, b) => b.end - b.start - (a.end - a.start) || a.start - b.start);

    const kept: Match[] = [];
    for (const match of matches) {
        const overlaps = kept.some((k) => match.start < k.end && match.end > k.start);
        if (!overlaps) kept.push(match);
    }

    kept.sort((a, b) => a.start - b.start);
    return kept;
};

/**
 * Split an example sentence into plain and highlighted runs.
 *
 * `subjectWord` (the entry the example belongs to) is highlighted as well,
 * but flagged with `isSubject` so the UI can style it differently from the
 * other deck words it surfaces.
 */
export const buildExampleSegments = (
    text: string,
    words: string[] = [],
    subjectWord?: string | null,
): ExampleSegment[] => {
    if (!text) return [];

    const lower = text.toLowerCase();
    // A lower-cased string of a different length means the text contains
    // characters whose case folds to several code points (e.g. "İ"). The
    // offsets would no longer line up, so skip highlighting entirely.
    if (lower.length !== text.length) return [{ text }];

    const subject = subjectWord?.trim().toLowerCase() ?? "";
    const candidates: { word: string; isSubject: boolean }[] = [];
    if (subject.length >= 2) candidates.push({ word: subject, isSubject: true });
    for (const word of words) {
        const trimmed = word?.trim();
        if (!trimmed) continue;
        const key = trimmed.toLowerCase();
        if (candidates.some((c) => c.word === key)) continue;
        candidates.push({ word: key, isSubject: false });
    }

    const matches = findMatches(text, lower, candidates);
    if (matches.length === 0) return [{ text }];

    const segments: ExampleSegment[] = [];
    let cursor = 0;
    for (const match of matches) {
        if (match.start > cursor) {
            segments.push({ text: text.slice(cursor, match.start) });
        }
        segments.push({
            text: text.slice(match.start, match.end),
            word: match.word,
            isSubject: subject !== "" && match.word === subject,
        });
        cursor = match.end;
    }
    if (cursor < text.length) {
        segments.push({ text: text.slice(cursor) });
    }

    return segments;
};

/**
 * True when the example contains at least one deck word worth highlighting.
 * Used to decide whether a surface should render the highlighted variant at
 * all (an example with no deck words reads better as plain text).
 */
export const hasDeckWords = (text: string, words: string[] = [], subjectWord?: string | null): boolean =>
    buildExampleSegments(text, words, subjectWord).some((segment) => segment.word !== undefined);
